import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEdit } from "react-icons/fa";
import CompanyLogo from "../components/CompanyLogo";
import AuthFooter from "../components/AuthFooter";
import { useDomain } from "../contexts/DomainContext";
import { getDisplayCompanyName } from "../utils/branding";
import "./EmailVerification.css";

// Email validation helper
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { domainConfig } = useDomain();
  
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userId, setUserId] = useState(null);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Get data from location state or localStorage
  useEffect(() => {
    const state = location.state;
    console.log("📧 EmailVerification received state:", state);
    
    if (state?.email) {
      console.log("✅ Setting from state - Email:", state.email, "Company:", state.companyName);
      // Set values directly without swapping (companyName may legitimately be empty)
      setEmail(state.email);
      setCompanyName(state.companyName || "");
      setUserId(state.userId);
      // Don't auto-set verificationSent - only set after API call
    } else {
      // Try to get from localStorage as fallback
      const storedEmail = localStorage.getItem("kit19_pending_email");
      const storedCompany = localStorage.getItem("kit19_pending_company");
      const storedUserId = localStorage.getItem("kit19_user_id");

      console.log("💾 Loading from localStorage - Email:", storedEmail, "Company:", storedCompany);

      if (storedEmail) {
        setEmail(storedEmail);
        setCompanyName(storedCompany || "");
        setUserId(storedUserId);
        // Check if verification was already sent in this session
        const wasSent = localStorage.getItem("kit19_verification_sent");
        if (wasSent === "true") {
          setVerificationSent(true);
        }
      } else {
        // No verification data, redirect back to login
        navigate("/login" + window.location.search);
      }
    }
  }, [location.state, navigate]);

  const validateEmailField = () => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      toast.error("Email is required");
      return false;
    }

    if (!isValidEmail(trimmedEmail)) {
      console.log("❌ Email validation failed for:", trimmedEmail);
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const validateCompanyField = () => {
    const trimmedCompany = companyName.trim();

    if (!trimmedCompany) {
      toast.error("Company name is required");
      return false;
    }

    return true;
  };

  const validateFields = () => validateEmailField() && validateCompanyField();

  const handleEditEmail = () => {
    setIsEditingEmail(true);
  };

  const handleConfirmEmail = async () => {
    if (!validateEmailField()) {
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedCompany = companyName.trim();
    
    console.log("📤 Sending to API:", {
      Email: trimmedEmail,
      CompanyName: trimmedCompany,
      UserId: userId,
    });

    setLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_SSO_API_URL || "https://auth.kit19.com/api/";

      const response = await fetch(apiUrl + "Authorize/EmailInfo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Email: trimmedEmail,
          CompanyName: trimmedCompany,
          UserId: userId,
        }),
      });

      const data = await response.json();
      console.log("📥 API Response:", data, "Status:", response.status);

      if (response.status === 200 && data === true) {
        toast.success("Email verified successfully!");
        setIsEditingEmail(false);
        setVerificationSent(true);
        // Optionally store the verified email
        localStorage.setItem("kit19_pending_email", trimmedEmail);
        localStorage.setItem("kit19_pending_company", trimmedCompany);
        localStorage.setItem("kit19_verification_sent", "true");
      } else {
        toast.error("Email verification failed. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying email:", error);
      toast.error("Failed to verify email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditCompany = () => {
    setIsEditingCompany(true);
  };

  const handleConfirmCompany = async () => {
    if (!validateCompanyField()) {
      return;
    }

    setLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_SSO_API_URL || "https://auth.kit19.com/api/";

      const response = await fetch(apiUrl + "Authorize/EmailInfo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Email: email.trim(),
          CompanyName: companyName.trim(),
          UserId: userId,
        }),
      });

      const data = await response.json();

      if (response.status === 200 && data === true) {
        toast.success("Company name verified successfully!");
        setIsEditingCompany(false);
        setVerificationSent(true);
        // Optionally store the verified company
        localStorage.setItem("kit19_pending_email", email.trim());
        localStorage.setItem("kit19_pending_company", companyName.trim());
        localStorage.setItem("kit19_verification_sent", "true");
      } else {
        toast.error("Verification failed. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying company:", error);
      toast.error("Failed to verify. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmail = () => {
    setIsEditingEmail(false);
    // Reset to stored value if cancel
    const storedEmail = localStorage.getItem("kit19_pending_email");
    if (storedEmail) {
      setEmail(storedEmail);
    }
  };

  const handleCancelCompany = () => {
    setIsEditingCompany(false);
    // Reset to stored value if cancel
    const storedCompany = localStorage.getItem("kit19_pending_company");
    if (storedCompany) {
      setCompanyName(storedCompany);
    }
  };

  const handleSendVerification = async () => {
    if (!validateFields()) {
      return;
    }

    const trimmedEmail = email.trim();
    const trimmedCompany = companyName.trim();
    
    console.log("📤 Sending verification email to API:", {
      UserId: userId,
      EmailAddress: trimmedEmail,
    });

    setLoading(true);
    try {
      const apiUrl =
        process.env.REACT_APP_SSO_API_URL || "https://auth.kit19.com/api/";

      const response = await fetch(apiUrl + "Authorize/SendEmailVerification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          UserId: userId,
          EmailAddress: trimmedEmail,
        }),
      });

      const data = await response.json();
      console.log("📥 SendEmailVerification Response:", data, "Status:", response.status);

      if (data?.isSuccess) {
        toast.success(data?.message || "Verification link sent to your email!");
        setVerificationSent(true);
        // Store verification ID and email for reference
        localStorage.setItem("kit19_pending_email", trimmedEmail);
        localStorage.setItem("kit19_pending_company", trimmedCompany);
        localStorage.setItem("kit19_verification_sent", "true");
        if (data?.verificationId) {
          localStorage.setItem("kit19_verification_id", data.verificationId);
        }
      } else {
        const errorMessage = data?.message || "Failed to send verification email";
        console.error("❌ Verification failed:", errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error sending verification email:", error);
      const errorMessage = error?.message || "Failed to send verification email. Please check your internet connection and try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    // Clear verification flag before refresh
    localStorage.removeItem("kit19_verification_sent");
    window.location.reload();
  };

  const handleBackToLogin = () => {
    // Clear all email verification related localStorage keys
    localStorage.removeItem("kit19_pending_email");
    localStorage.removeItem("kit19_pending_company");
    localStorage.removeItem("kit19_user_id");
    localStorage.removeItem("kit19_verification_sent");
    
    // Preserve URL parameters when navigating back
    navigate("/login" + window.location.search);
  };

  const displayCompanyName = getDisplayCompanyName(companyName);

  return (
    <div
      className="sso-verification-container"
      style={{
        backgroundImage: domainConfig?.backgroundImage
          ? `url(${domainConfig.backgroundImage})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="verification-card">
        <div className="verification-header">
          <h1>Email Verification</h1>
          <div className="header-divider"></div>
        </div>

        <div className="verification-content">
          {/* Email ID Field */}
          <div className="verification-field">
            <label>Email ID:</label>
            <div className="field-input-group">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!isEditingEmail}
                className={`verification-input ${isEditingEmail ? "editing" : ""}`}
                autoFocus={isEditingEmail}
              />
              <button
                className={`edit-btn ${isEditingEmail ? "confirm" : "edit"}`}
                onClick={isEditingEmail ? handleConfirmEmail : handleEditEmail}
                title={isEditingEmail ? "Confirm" : "Edit"}
                disabled={loading}
              >
                {isEditingEmail ? "✓" : <FaEdit />}
              </button>
              {isEditingEmail && (
                <button
                  className="edit-btn cancel"
                  onClick={handleCancelEmail}
                  title="Cancel"
                  disabled={loading}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Company Name Field */}
          <div className="verification-field">
            <label>Company Name</label>
            <div className="field-input-group">
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!isEditingCompany}
                className={`verification-input ${isEditingCompany ? "editing" : ""}`}
                autoFocus={isEditingCompany}
              />
              <button
                className={`edit-btn ${isEditingCompany ? "confirm" : "edit"}`}
                onClick={isEditingCompany ? handleConfirmCompany : handleEditCompany}
                title={isEditingCompany ? "Confirm" : "Edit"}
                disabled={loading}
              >
                {isEditingCompany ? "✓" : <FaEdit />}
              </button>
              {isEditingCompany && (
                <button
                  className="edit-btn cancel"
                  onClick={handleCancelCompany}
                  title="Cancel"
                  disabled={loading}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Verification Status */}
          {verificationSent && (
            <div className="verification-status">
              <p className="status-message">
                Verification Link Sent to Your Email
                <span
                  className="resend-link"
                  onClick={() => {
                    setVerificationSent(false);
                    localStorage.removeItem("kit19_verification_sent");
                  }}
                >
                  Resend
                </span>
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="verification-actions">
            <button
              className="btn-primary"
              onClick={handleSendVerification}
              disabled={loading || verificationSent || !email.trim() || !companyName.trim()}
            >
              {loading ? "Sending..." : verificationSent ? "Verification Sent" : "Send Verification"}
            </button>
            <button className="btn-secondary" onClick={handleRefresh}>
              Refresh
            </button>
          </div>

          {/* Back to Login Link */}
          <div className="back-to-login">
            <button
              type="button"
              className="link-button"
              onClick={handleBackToLogin}
            >
              Back to Login
            </button>
          </div>
        </div>

        <AuthFooter domainConfig={domainConfig} showSignUp={false} />
      </div>
    </div>
  );
};

export default EmailVerification;
